import * as api from "../api/client";
import { AppHeader, Breadcrumb } from "./AppHeader";
import { pixelTheme } from "../theme/pixel";
import { simplePanelStyle } from "../theme/simplePanel";

// All four reasons a `*EvictionReason` may take across activities.
// Per-activity types narrow this further, but we accept the union so
// one component handles every activity's eviction screen.
export type ActivityEvictionReason =
  | "acquired_by_sleep"
  | "acquired_by_battle"
  | "acquired_by_other_tab"
  | "idle"
  | "test_cleanup"
  | string;

interface Props {
  // Used in the "<activity> moved to another tab." message. e.g.
  // "Foraging" / "Cooking" — title-case, no trailing punctuation.
  activityLabel: string;
  // Optional copy shown below the eviction reason; cooking uses it to
  // reassure the player that the kitchen state is persistent. Forage
  // omits it because the orchard is rebuilt on next visit anyway.
  subtitle?: string;
  reason: ActivityEvictionReason;
  title: string;
  breadcrumb: string | null;
  parentPath: string;
  user: api.User | null;
}

// Replaces the activity view when the server tells us this tab's
// session has ended — another tab took over, the player started a
// battle / sleep elsewhere that auto-ended us, or the session went
// idle. One component covers every activity; the activity-specific
// page passes the label + optional subtitle.
export function ActivityEvictionPage({
  activityLabel,
  subtitle,
  reason,
  title,
  breadcrumb,
  parentPath,
  user,
}: Props) {
  const theme = pixelTheme;

  const message =
    reason === "acquired_by_other_tab"
      ? `${activityLabel} moved to another tab.`
      : reason === "acquired_by_sleep"
        ? "You're sleeping at the Inn now."
        : reason === "acquired_by_battle"
          ? "A battle started elsewhere."
          : `${activityLabel} session ended.`;

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "20px 24px",
      }}
    >
      <div
        style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gap: 16 }}
      >
        <AppHeader
          user={user}
          backTo={parentPath}
          backLabel="◀ BACK"
        />
        <Breadcrumb text={breadcrumb} />
        <div
          style={{
            ...simplePanelStyle(theme),
            padding: 24,
            display: "grid",
            gap: 16,
            justifyItems: "center",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 14,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: theme.accent,
            }}
          >
            {title}
          </div>
          <div style={{ fontSize: 16, color: theme.ink }}>{message}</div>
          {subtitle && (
            <div style={{ fontSize: 14, color: theme.inkFaint }}>{subtitle}</div>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              padding: "8px 16px",
              background: theme.panelSolid,
              color: theme.ink,
              border: `2px solid ${theme.ink}`,
              cursor: "pointer",
            }}
          >
            REFRESH TO RECLAIM
          </button>
        </div>
      </div>
    </div>
  );
}
